import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Input, Spinner } from "../components/ui";
import { Search, Sparkles, SlidersHorizontal, ChevronRight, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { buildMarketOptions, VIENNA_MARKET_VALUE, normalizeMarketSearch } from "../lib/marketMeta";
import { CONDITION_FACTORS, estimateRent } from "../lib/rentCalculator";

const CONDITION_OPTIONS = [
  { value: "basic", labelKey: "rentCalc.conditionBasic" },
  { value: "standard", labelKey: "rentCalc.conditionStandard" },
  { value: "good", labelKey: "rentCalc.conditionGood" },
  { value: "premium", labelKey: "rentCalc.conditionPremium" },
];

const FEATURE_OPTIONS = [
  { key: "balcony", labelKey: "rentCalc.featureBalcony" },
  { key: "lift", labelKey: "rentCalc.featureLift" },
  { key: "furnished", labelKey: "rentCalc.featureFurnished" },
];

export default function RentCalculator() {
  const { t } = useI18n();
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [marketSearch, setMarketSearch] = useState("");
  const [selectedMarket, setSelectedMarket] = useState(VIENNA_MARKET_VALUE);
  const [sizeM2, setSizeM2] = useState(50);
  const [condition, setCondition] = useState("standard");
  const [features, setFeatures] = useState({
    balcony: false,
    lift: false,
    furnished: false,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/markets");
        if (!mounted) return;
        setMarkets(data.markets || []);
      } catch (e) {
        if (!mounted) return;
        setErr(e?.response?.data?.detail || t("common.loadFailed"));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const marketOptions = buildMarketOptions(markets);
  const normalizedSearch = normalizeMarketSearch(marketSearch);
  const filteredOptions = normalizedSearch
    ? marketOptions.filter((opt) => opt.searchText.includes(normalizedSearch))
    : marketOptions;
  const activeOption = marketOptions.find((opt) => opt.value === selectedMarket) || marketOptions[0] || null;
  const estimate = estimateRent({
    markets,
    marketCode: activeOption?.value || selectedMarket,
    sizeM2,
    condition,
    ...features,
  });

  useEffect(() => {
    if (!marketOptions.length) return;
    setSelectedMarket((current) => {
      if (marketOptions.some((opt) => opt.value === current)) return current;
      return marketOptions.find((opt) => opt.value === VIENNA_MARKET_VALUE)?.value || marketOptions[0]?.value || current;
    });
  }, [marketOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="p-10"><Spinner /></div>;
  }

  if (err) {
    return (
      <div className="p-10 text-center">
        <p className="text-sm text-signal-red font-mono">{err}</p>
        <button onClick={() => window.location.reload()} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
          {t("common.retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-6 border-b border-border bg-grid flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div className="max-w-3xl">
          <div className="overline">{t("rentCalc.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("rentCalc.title")}</h1>
          <p className="mt-2 text-xs text-text-muted max-w-2xl leading-relaxed">
            {t("rentCalc.subtitle")}
          </p>
          <p className="mt-2 text-[11px] font-mono text-text-muted">
            {t("rentCalc.calibrationNote")}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-px border border-border bg-border w-full lg:w-[360px]">
          <Stat label={t("rentCalc.marketCount")} value={markets.length.toLocaleString()} sub={t("rentCalc.marketCountSub", { count: markets.length })} />
          <Stat label={t("rentCalc.selectedMarket")} value={activeOption?.label || "—"} sub={estimate.isViennaConsolidated ? t("rentCalc.viennaNote", { count: estimate.benchmarkCount }) : t("rentCalc.marketBenchmark", { value: estimate.benchmarkPerSqm })} />
        </div>
      </div>

      <div className="grid grid-cols-12">
        <aside className="col-span-12 lg:col-span-4 border-r border-border p-6 space-y-6">
          <section className="space-y-3">
            <div className="flex items-center gap-2 overline text-text-muted">
              <Search size={12} />
              {t("rentCalc.marketLabel")}
            </div>
            <Input
              data-testid="rentcalc-market-search"
              value={marketSearch}
              onChange={(e) => setMarketSearch(e.target.value)}
              placeholder={t("rentCalc.marketSearch")}
            />
            <div className="border border-border bg-bg">
              <div className="max-h-72 overflow-y-auto divide-y divide-border">
                {filteredOptions.length ? filteredOptions.map((opt) => {
                  const active = opt.value === activeOption?.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`rentcalc-option-${opt.value}`}
                      aria-pressed={active}
                      onClick={() => setSelectedMarket(opt.value)}
                      className={`w-full text-left px-3 py-3 transition-colors flex items-start justify-between gap-3 ${
                        active ? "bg-signal-green/10 text-white" : "hover:bg-surfaceHover text-text-secondary"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className={`text-sm ${active ? "text-white" : "text-white"}`}>{opt.label}</div>
                        <div className="text-[10px] font-mono text-text-muted mt-1">
                          {opt.synthetic ? t("rentCalc.viennaSynthetic") : opt.country}
                        </div>
                      </div>
                      {active && <ChevronRight size={14} className="text-signal-green shrink-0 mt-0.5" />}
                    </button>
                  );
                }) : (
                  <div className="px-3 py-4 text-[11px] font-mono text-text-muted">
                    {t("rentCalc.noMarketMatches", { query: marketSearch })}
                  </div>
                )}
              </div>
            </div>
            <div className="text-[11px] font-mono text-text-muted">
              {activeOption?.value === VIENNA_MARKET_VALUE
                ? t("rentCalc.viennaNote", { count: estimate.benchmarkCount })
                : t("rentCalc.marketNote", { value: activeOption?.label || "—" })}
            </div>
          </section>

          <section className="space-y-3 border-t border-border pt-6">
            <div className="flex items-center gap-2 overline text-text-muted">
              <SlidersHorizontal size={12} />
              {t("rentCalc.sizeLabel")}
            </div>
            <div className="grid grid-cols-[1fr_auto] items-end gap-3">
              <div className="space-y-1">
                <Input
                  data-testid="rentcalc-size"
                  type="number"
                  min="0"
                  step="1"
                  value={sizeM2}
                  onChange={(e) => setSizeM2(parseFloat(e.target.value) || 0)}
                />
                <div className="text-[11px] font-mono text-text-muted">
                  {t("rentCalc.sizeHint")}
                </div>
              </div>
              <div className="text-right">
                <div className="overline">{t("rentCalc.perSqmLabel")}</div>
                <div className="num text-lg text-white">{estimate.estimatedPerSqm ? `€${estimate.estimatedPerSqm.toFixed(2)}` : "—"}</div>
              </div>
            </div>
          </section>

          <section className="space-y-3 border-t border-border pt-6">
            <div className="overline">{t("rentCalc.conditionLabel")}</div>
            <div className="grid grid-cols-2 gap-2">
              {CONDITION_OPTIONS.map((opt) => {
                const active = condition === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCondition(opt.value)}
                    className={`border px-3 py-2 text-left transition-colors ${
                      active ? "border-signal-green bg-signal-green/10 text-white" : "border-border bg-bg text-text-muted hover:text-white hover:border-signal-green/40"
                    }`}
                  >
                    <div className="text-sm">{t(opt.labelKey)}</div>
                    <div className="text-[10px] font-mono text-text-muted mt-1">
                      {t("rentCalc.factorValue", { value: CONDITION_FACTORS[opt.value].toFixed(2) })}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="space-y-3 border-t border-border pt-6">
            <div className="overline">{t("rentCalc.featureLabel")}</div>
            <div className="space-y-2">
              {FEATURE_OPTIONS.map((opt) => (
                <label key={opt.key} className="flex items-center justify-between gap-3 border border-border bg-bg px-3 py-2 text-sm cursor-pointer">
                  <span>{t(opt.labelKey)}</span>
                  <input
                    type="checkbox"
                    checked={features[opt.key]}
                    onChange={(e) => setFeatures((prev) => ({ ...prev, [opt.key]: e.target.checked }))}
                    style={{ accentColor: "#00FF66" }}
                  />
                </label>
              ))}
            </div>
          </section>
        </aside>

        <main className="col-span-12 lg:col-span-8 p-6">
          <div className="grid gap-6">
            <section className="relative overflow-hidden border border-border bg-surface p-6 ai-glow">
              <div className="absolute inset-x-0 top-0 h-px bg-signal-green/40" />
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-signal-green/10 blur-3xl" />
              <div className="absolute -left-8 bottom-0 w-40 h-40 rounded-full bg-white/5 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-2 overline text-signal-green">
                  <Sparkles size={12} />
                  {t("rentCalc.summaryLabel")}
                </div>
                <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                  <div>
                    <div className="text-[11px] font-mono text-text-muted">{t("rentCalc.monthlyLabel")}</div>
                    <div className="mt-2 flex items-end gap-3">
                      <div className="num text-5xl md:text-7xl font-light tracking-tight text-white">
                        {estimate.estimatedMonthly ? `€${estimate.estimatedMonthly.toLocaleString()}` : "—"}
                      </div>
                      <div className="pb-3 text-xs font-mono text-text-muted">{t("rentCalc.perMonth")}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] font-mono text-text-muted">{t("rentCalc.annualLabel")}</div>
                    <div className="num text-xl md:text-2xl text-white mt-1">
                      {estimate.estimatedAnnual ? `€${estimate.estimatedAnnual.toLocaleString()}` : "—"}
                    </div>
                    <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono ${estimate.deltaMonthly >= 0 ? "text-signal-red" : "text-signal-green"}`}>
                      {estimate.deltaMonthly >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                      {t("rentCalc.deltaLabel")}: {estimate.deltaMonthly >= 0 ? "+" : ""}€{estimate.deltaMonthly.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
                  <Stat
                    label={t("rentCalc.benchmarkPerSqm")}
                    value={estimate.benchmarkPerSqm ? `€${estimate.benchmarkPerSqm.toFixed(2)}` : "—"}
                    sub={activeOption?.label || "—"}
                  />
                  <Stat
                    label={t("rentCalc.sizeFactorLabel")}
                    value={estimate.sizeFactor.toFixed(2)}
                    sub={t("rentCalc.sizeFactorSub")}
                  />
                  <Stat
                    label={t("rentCalc.featureFactorLabel")}
                    value={estimate.totalFactor.toFixed(2)}
                    sub={t("rentCalc.totalFactorSub", { condition: estimate.conditionFactor.toFixed(2), features: estimate.featureFactor.toFixed(2) })}
                  />
                </div>
              </div>
            </section>

            <section className="border border-border bg-bg">
              <div className="px-4 py-3 border-b border-border bg-surface flex items-center justify-between gap-3">
                <div className="overline text-text-muted">{t("rentCalc.breakdownTitle")}</div>
                <div className="text-[10px] font-mono text-text-muted">
                  {estimate.isViennaConsolidated ? t("rentCalc.viennaBreakdown", { count: estimate.benchmarkCount }) : activeOption?.label || "—"}
                </div>
              </div>
              <div className="divide-y divide-border">
                <DetailRow testId="rentcalc-row-market-median" label={t("rentCalc.rowMarketMedian")} value={estimate.benchmarkPerSqm ? `€${estimate.benchmarkPerSqm.toFixed(2)}/m²` : "—"} />
                <DetailRow label={t("rentCalc.rowSizeFactor")} value={estimate.sizeFactor.toFixed(2)} />
                <DetailRow label={t("rentCalc.rowConditionFactor")} value={estimate.conditionFactor.toFixed(2)} />
                <DetailRow label={t("rentCalc.rowFeatureFactor")} value={estimate.featureFactor.toFixed(2)} />
                <DetailRow label={t("rentCalc.rowEstimatedSqm")} value={estimate.estimatedPerSqm ? `€${estimate.estimatedPerSqm.toFixed(2)}/m²` : "—"} />
                <DetailRow testId="rentcalc-monthly" label={t("rentCalc.rowMonthly")} value={estimate.estimatedMonthly ? `€${estimate.estimatedMonthly.toLocaleString()}` : "—"} accent="green" />
                <DetailRow label={t("rentCalc.rowAnnual")} value={estimate.estimatedAnnual ? `€${estimate.estimatedAnnual.toLocaleString()}` : "—"} />
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="p-4 border border-border bg-bg">
      <div className="overline">{label}</div>
      <div className="mt-2 num text-xl md:text-2xl text-white">{value}</div>
      {sub && <div className="mt-1 text-[11px] font-mono text-text-muted">{sub}</div>}
    </div>
  );
}

function DetailRow({ label, value, accent = "white", testId }) {
  const cls = accent === "green" ? "text-signal-green" : accent === "yellow" ? "text-signal-yellow" : "text-white";
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3" data-testid={testId}>
      <div className="text-[11px] font-mono text-text-muted uppercase tracking-widest2">{label}</div>
      <div className={`num text-sm ${cls}`}>{value}</div>
    </div>
  );
}
